import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Users,
  Star,
  CircleDashed,
  Compass,
  Briefcase,
  Clock,
  Info,
  AlertTriangle,
  Loader2,
  ChevronDown,
  ListChecks,
  Minus,
  X,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import AppSelect from 'src/components/common/AppSelect';
import { Button } from 'src/components/ui/button';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import { Alert } from 'src/components/common/Alert';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogTitle,
  DialogDescription,
} from 'src/components/ui/dialog';
import {
  useRequirementMatches,
  useRequirementConstraintModes,
} from 'src/services/api/queries/candidateQueries';
import {
  useSubmitCandidates,
  useSubmitSecondaryCandidates,
  useSetConstraintMode,
  useRemoveConstraintMode,
} from 'src/services/api/mutations/candidateMutations';
import { useRequirementLocations } from 'src/services/api/queries/opportunityQueries';
import { useToast } from 'src/hooks/use-toast';
import { useAuth } from 'src/hooks/useAuth';
import { cn } from 'src/lib/utils';
import CandidateProfileModal from 'src/components/Modals/CandidateProfileModal';
import SearchInput from 'src/components/common/SearchInput';
import CandidateMatchRow from './CandidateMatchRow';
import { tintFrom, TIERS } from './matchData';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'best', label: 'Best Match' },
  { key: 'near', label: 'Near Match' },
  { key: 'explore', label: 'Explore' },
];

const SummaryCard = ({ icon, iconColor, label, value, sub }) => (
  <div className="flex flex-1 min-w-[180px] items-start gap-3.5 bg-card rounded-xl border border-border p-[18px_20px] shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
    <span
      className="w-10 h-10 rounded-[10px] flex items-center justify-center shrink-0"
      style={{ color: iconColor, backgroundColor: tintFrom(iconColor, 14) }}
    >
      {icon}
    </span>
    <div>
      <div className="text-[11.5px] font-medium text-muted-foreground tracking-[0.02em] mb-0.5">
        {label}
      </div>
      <div className="text-[26px] font-bold text-foreground-strong tabular-nums tracking-[-0.03em] leading-[1.2]">
        {value ?? 0}
      </div>
      {sub && (
        <div className="text-[11.5px] text-muted-foreground font-medium mt-0.5">
          {sub}
        </div>
      )}
    </div>
  </div>
);

// Dot colour per mode. `off` = no rule → no dot (returns null).
const modeDotColor = (mode) => {
  if (mode === 'drop') return '#DC2626';
  if (mode === 'flag') return '#B45309';
  if (mode === 'boost') return '#0D9668';
  return null;
};

// Segmented match modes, mirroring the requirement-tuning template:
//   Off   — no rule; shown but doesn't filter or rank
//   Drop  — hard filter; candidates who miss it are removed
//   Flag  — kept but marked with a score penalty
//   Boost — treated as a bonus; those who have it rank higher
const RULE_MODES = [
  {
    key: 'off',
    label: 'Off',
    Icon: Minus,
    selText: 'text-foreground-strong',
    hint: 'no rule set. This still shows on the requirement, but doesn’t filter or rank anyone.',
  },
  {
    key: 'drop',
    label: 'Drop',
    Icon: X,
    selText: 'text-ds-danger',
    hint: 'candidates who don’t meet this are removed from results and listed under “Review hidden.” Use for true dealbreakers only.',
  },
  {
    key: 'flag',
    label: 'Flag',
    Icon: CircleDashed,
    selText: 'text-ds-warning',
    hint: 'candidates are kept and shown with a pass/fail mark plus a score penalty. Nothing is silently removed.',
  },
  {
    key: 'boost',
    label: 'Boost',
    Icon: TrendingUp,
    selText: 'text-ds-success',
    hint: 'treated as a bonus. Candidates who have it rank higher; no one is penalised or removed.',
  },
];

// The exact :field enum the constraint-modes PUT/DELETE endpoint accepts
// (from the API's own validation error) — "location" is NOT among them.
const CONSTRAINT_API_FIELDS = [
  'notice',
  'ctc',
  'experience',
  'education',
  'domain',
  'leadership',
  'certifications',
  'languages',
];

// A Match-Rule chip: shows a mode dot + caret, and opens a segmented
// Off / Drop / Flag / Boost popover with a per-mode hint and a Save action.
// `field` + `requirementId` are only set on the API-backed constraint chips
// (Experience/Compensation/Notice period) — when present AND `field` is one
// of CONSTRAINT_API_FIELDS, Save calls the real constraint-modes endpoint;
// otherwise it's local-only (e.g. the skill chips, which have no such field).
const RuleChip = ({
  mode,
  label,
  onModeChange,
  isUnresolved,
  allowedModes,
  field,
  requirementId,
  className,
  children,
  interactive = true,
}) => {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const dotColor = interactive ? modeDotColor(mode) : null;
  const current = RULE_MODES.find((m) => m.key === mode) || RULE_MODES[0];
  const setConstraintMode = useSetConstraintMode();
  const removeConstraintMode = useRemoveConstraintMode();
  const saving = setConstraintMode.isPending || removeConstraintMode.isPending;
  const canPersist =
    !!field && !!requirementId && CONSTRAINT_API_FIELDS.includes(field);
  // Off (remove the rule) is always offered; every other mode only renders
  // when it's present in the field's allowed_modes from the API. When no
  // allowedModes list is given (skills, or fields the API doesn't cover),
  // all four modes show — there's nothing to restrict against.
  const visibleModes = Array.isArray(allowedModes)
    ? RULE_MODES.filter((m) => m.key === 'off' || allowedModes.includes(m.key))
    : RULE_MODES;

  const handleSave = async () => {
    if (!canPersist) {
      toast({
        title: 'Rule updated',
        description:
          mode === 'off'
            ? `${label} — rule removed (this view)`
            : `${label} — set to ${current.label} (this view)`,
      });
      setOpen(false);
      return;
    }
    try {
      if (mode === 'off') {
        await removeConstraintMode.mutateAsync({ requirementId, field });
      } else {
        await setConstraintMode.mutateAsync({ requirementId, field, mode });
      }
      toast({
        title: 'Rule updated',
        description:
          mode === 'off'
            ? `${label} — rule removed`
            : `${label} — set to ${current.label}`,
      });
      setOpen(false);
    } catch (err) {
      toast({
        title: 'Error',
        description: err.message || `Failed to update the ${label} rule`,
        variant: 'destructive',
      });
    }
  };

  // Must-have/nice-to-have skill chips are shown read-only for now — no mode
  // dot and no mode-change popover, just the chip label itself.
  if (!interactive) {
    return (
      <span className={cn('inline-flex items-center gap-[5px]', className)}>
        {children}
      </span>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-[5px] cursor-pointer transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
            mode === 'drop' && 'border-ds-danger',
            className
          )}
        >
          {dotColor && (
            <span
              className="w-[7px] h-[7px] rounded-full flex-shrink-0"
              style={{
                background: dotColor,
                boxShadow: '0 0 0 2px rgba(255,255,255,0.6)',
              }}
            />
          )}
          {children}
          <ChevronDown
            className="w-[11px] h-[11px] opacity-50 flex-shrink-0"
            strokeWidth={2.2}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[264px] p-3.5">
        <div className="text-[13px] font-[650] text-foreground-strong tracking-[-0.01em] mb-2.5">
          {label}
        </div>

        {isUnresolved && (
          <div className="flex items-start gap-1.5 text-[11px] leading-[1.45] text-muted-foreground bg-muted border border-border rounded-[7px] px-2 py-[7px] mb-3">
            <AlertTriangle
              className="w-3 h-3 flex-shrink-0 mt-px"
              strokeWidth={2}
            />
            <span>
              Not linked to the skill library — matched on name text only.
              Spelling variants may be missed.
            </span>
          </div>
        )}

        <div
          role="radiogroup"
          aria-label="How this affects matching"
          className="grid gap-[3px] bg-muted rounded-[9px] p-1"
          style={{
            gridTemplateColumns: `repeat(${visibleModes.length}, minmax(0, 1fr))`,
          }}
        >
          {visibleModes.map((m) => {
            const sel = m.key === mode;
            const { Icon } = m;
            return (
              <button
                key={m.key}
                type="button"
                role="radio"
                aria-checked={sel}
                onClick={() => onModeChange(m.key)}
                className={cn(
                  'flex flex-col items-center gap-[3px] rounded-md py-[7px] px-0.5 text-[11px] font-[550] transition-colors',
                  sel
                    ? `bg-card shadow-[0_1px_3px_rgba(0,0,0,0.12)] ${m.selText}`
                    : 'text-muted-foreground hover:text-foreground-strong'
                )}
              >
                <Icon className="w-[13px] h-[13px]" strokeWidth={2} />
                {m.label}
              </button>
            );
          })}
        </div>

        <div className="text-[11px] leading-[1.5] text-muted-foreground mt-2.5 py-2 px-2.5 rounded-lg bg-brand-bg-subtle border border-border">
          <b className="text-foreground-strong font-semibold">
            {current.label}
          </b>{' '}
          — {current.hint}
        </div>

        <div className="flex items-center justify-end mt-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="text-[11.5px] font-semibold text-primary bg-brand-bg-subtle border border-primary/20 rounded-[7px] px-[11px] py-1.5 hover:bg-primary hover:text-primary-foreground transition-colors disabled:opacity-50 disabled:pointer-events-none"
          >
            {/* eslint-disable-next-line no-nested-ternary */}
            {saving ? 'Saving…' : mode === 'off' ? 'Remove rule' : 'Save rule'}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// ── "How modes work" explainer content (mirrors the requirement template) ──
const MODE_NAME_CLS = {
  off: 'text-foreground-strong',
  drop: 'text-ds-danger',
  flag: 'text-ds-warning',
};

const MODE_EXPLAIN = [
  {
    key: 'off',
    name: 'Off',
    text: 'No rule set. The constraint still shows as a pass/fail chip on each candidate, but removes no one and moves no score. This is where every constraint starts.',
  },
  {
    key: 'drop',
    name: 'Drop',
    text: 'Candidates who miss it are removed from the list and collected under “Review hidden” with a reason. Always restorable — nothing disappears silently.',
  },
  {
    key: 'flag',
    name: 'Flag',
    text: 'Candidates are kept and marked. Each definitive miss costs a score penalty, so they slide down the ranking rather than out of it.',
  },
];

const FIELD_EXPLAIN = [
  {
    label: 'Experience',
    Icon: Briefcase,
    drop: 'Candidates outside the range are removed before ranking and listed under “Review hidden,” restorable at any time.',
    dropSub:
      'Dropping happens before the shortlist is built, so each removal frees a slot for the next viable candidate. Anyone whose experience didn’t parse is kept, never dropped.',
    flag: 'The candidate stays in the list and takes a score penalty per miss. They slide down the ranking instead of disappearing.',
    flagSub:
      'Unknown experience never costs anything. A candidate already capped at Explore stays capped — flagging can lower a tier, never raise one.',
    boost:
      'More experience isn’t better — being inside the range only means being eligible, so a boost would pay candidates for meeting the bar. Experience fit is already scored continuously, so a bonus would count the same signal twice.',
  },
  {
    label: 'Notice period',
    Icon: Clock,
    drop: 'Candidates over the limit are removed before ranking and listed under “Review hidden,” restorable at any time.',
    dropSub:
      'Everyone whose notice is unknown stays in the list — so a long-notice name still showing up is missing data, not a broken filter.',
    flag: 'The candidate stays in the list and takes a score penalty per miss. A long-notice candidate can fall a tier but stays callable.',
    flagSub:
      'Unknown notice never costs anything. Use this when the start date is negotiable — you still see them, just not first.',
    boost:
      'Joinability is table stakes, not quality. A shorter notice doesn’t make someone a better hire — both meet the requirement. Sort by notice period if that’s the order you want.',
  },
  {
    label: 'Compensation',
    Icon: Wallet,
    drop: 'Candidates asking above the ceiling are removed before ranking and listed under “Review hidden,” restorable at any time.',
    dropSub:
      'A small tolerance applies, so an ask just over budget still survives. Unknown asks are kept and counted on the hidden banner.',
    flag: 'The candidate stays in the list and takes a score penalty per miss. Over-budget people stay visible, because many of them negotiate.',
    flagSub:
      'Unknown CTC never costs anything. The same tolerance decides what counts as a miss.',
    boost:
      'Budget is a gate, not a virtue — a cheaper candidate isn’t a better one. Boosting on CTC would train the list to prefer whoever quotes lowest, which serves neither you nor the candidate.',
  },
];

const BOOST_LEAD =
  'Boost would add points for meeting a constraint. For all three of these, meeting it is the baseline rather than a mark of quality — and rewarding it would distort the ranking in a specific, predictable way.';
const BOOST_STATUS =
  'Boost isn’t switched off for these fields alone — it’s off across the board for now. Its intended home is the preferred tier of certifications, education and languages, where “has the cert we’d like” earns a small bonus. Until then a boost could only ever reward mandatory items — and meeting a mandatory requirement is table stakes, not a bonus.';

const ModeDot = ({ mode }) => (
  <span
    className="w-[7px] h-[7px] rounded-full inline-block flex-shrink-0"
    style={{ background: modeDotColor(mode) || 'var(--border)' }}
  />
);

// Section-level explainer: how the modes work and why Boost isn't offered.
const HowModesWork = () => {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Info className="w-[13px] h-[13px]" strokeWidth={2} />
      </DialogTrigger>
      <DialogContent className="max-w-[720px]">
        <DialogHeader>
          <DialogTitle>How constraint modes work</DialogTitle>
          <DialogDescription>
            Each constraint carries one enforcement mode. It decides whether a
            candidate who misses it is removed, kept and marked down, or simply
            shown as-is.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <p className="text-[12px] leading-[1.6] text-muted-foreground mb-4">
            Modes act at different points.{' '}
            <b className="text-foreground-strong font-semibold">Drop</b> runs
            before the shortlist is built, so it changes who competes — every
            removal frees a slot for the next viable candidate.{' '}
            <b className="text-foreground-strong font-semibold">Flag</b> runs
            after, on the people already pulled in, so it only re-orders them.
            In every mode, missing data is neutral: a candidate whose notice,
            CTC or experience didn’t parse is never removed and never penalised.
          </p>

          <p className="text-[10px] font-[650] uppercase tracking-[0.07em] text-muted-foreground mb-2.5">
            The modes
          </p>
          <div className="flex flex-col gap-2 mb-5">
            {MODE_EXPLAIN.map((m) => (
              <div
                key={m.key}
                className="grid grid-cols-[74px_1fr] gap-3 items-start rounded-[9px] bg-muted border border-border p-2.5"
              >
                <span
                  className={cn(
                    'inline-flex items-center gap-1.5 text-[11.5px] font-[650]',
                    MODE_NAME_CLS[m.key]
                  )}
                >
                  <ModeDot mode={m.key} />
                  {m.name}
                </span>
                <span className="text-[11.5px] leading-[1.55] text-muted-foreground">
                  {m.text}
                </span>
              </div>
            ))}
          </div>

          <p className="text-[10px] font-[650] uppercase tracking-[0.07em] text-muted-foreground mb-2.5">
            What that means field by field
          </p>
          <div className="rounded-[9px] border border-border overflow-hidden mb-5">
            <div className="hidden sm:grid grid-cols-[136px_1fr_1fr] bg-muted">
              <div className="px-3 py-2 text-[10px] font-[650] uppercase tracking-[0.06em] text-muted-foreground">
                Constraint
              </div>
              <div className="px-3 py-2 text-[10px] font-[650] uppercase tracking-[0.06em] text-muted-foreground border-l border-border">
                Set to Drop
              </div>
              <div className="px-3 py-2 text-[10px] font-[650] uppercase tracking-[0.06em] text-muted-foreground border-l border-border">
                Set to Flag
              </div>
            </div>
            {FIELD_EXPLAIN.map((f) => (
              <div
                key={f.label}
                className="grid grid-cols-1 sm:grid-cols-[136px_1fr_1fr] border-t border-border"
              >
                <div className="px-3 py-2.5 flex items-center gap-1.5 text-[11.5px] font-semibold text-foreground-strong">
                  <f.Icon
                    className="w-3 h-3 text-muted-foreground flex-shrink-0"
                    strokeWidth={2}
                  />
                  {f.label}
                </div>
                <div className="px-3 py-2.5 border-t sm:border-t-0 sm:border-l border-border text-[11.5px] text-muted-foreground leading-[1.5]">
                  <b className="sm:hidden text-ds-danger font-semibold">
                    Drop —{' '}
                  </b>
                  {f.drop}
                  <span className="block mt-1 text-[10.5px] opacity-80">
                    {f.dropSub}
                  </span>
                </div>
                <div className="px-3 py-2.5 border-t sm:border-t-0 sm:border-l border-border text-[11.5px] text-muted-foreground leading-[1.5]">
                  <b className="sm:hidden text-ds-warning font-semibold">
                    Flag —{' '}
                  </b>
                  {f.flag}
                  <span className="block mt-1 text-[10.5px] opacity-80">
                    {f.flagSub}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-[10px] border border-border bg-muted p-3.5">
            <div className="flex items-center gap-1.5 mb-1.5">
              <ModeDot mode="off" />
              <span className="text-[12px] font-[650] text-foreground-strong">
                Why Boost isn’t offered
              </span>
              <span className="ml-auto text-[9.5px] font-[650] uppercase tracking-[0.05em] px-[7px] py-0.5 rounded bg-border text-muted-foreground">
                Not available
              </span>
            </div>
            <p className="text-[11.5px] leading-[1.55] text-muted-foreground mb-3">
              {BOOST_LEAD}
            </p>
            <div className="flex flex-col gap-2.5">
              {FIELD_EXPLAIN.map((f) => (
                <div
                  key={f.label}
                  className="grid grid-cols-1 sm:grid-cols-[108px_1fr] gap-1 sm:gap-2.5"
                >
                  <span className="text-[11.5px] font-semibold text-foreground-strong flex items-center gap-1.5">
                    <f.Icon
                      className="w-3 h-3 text-muted-foreground flex-shrink-0"
                      strokeWidth={2}
                    />
                    {f.label}
                  </span>
                  <span className="text-[11.5px] leading-[1.55] text-muted-foreground">
                    {f.boost}
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-3.5 pt-3 border-t border-border text-[11px] leading-[1.55] text-muted-foreground">
              {BOOST_STATUS}
            </p>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

const formatExpRange = (range) => {
  if (!range) return range;
  if (range.includes('∞')) {
    const match = range.match(/(\d+)/);
    return match ? `${match[1]}+ yrs` : range;
  }
  return range;
};

const SubmitCandidate = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const { getUserInfo } = useAuth();
  const opportunityId = searchParams.get('opportunityId');
  const assignmentId = searchParams.get('assignmentId');
  const requirementLocationIdFromParams = searchParams.get(
    'requirementLocationId'
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
  const [selectedRequirementLocation, setSelectedRequirementLocation] =
    useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [summaryOpen, setSummaryOpen] = useState(true);
  const [rulesOpen, setRulesOpen] = useState(true);
  // Per-item match-rule modes (flag | boost | drop), keyed by rule id.
  const [rules, setRules] = useState({});
  const setRuleMode = (key, mode) =>
    setRules((prev) => ({ ...prev, [key]: mode }));
  const [profileModal, setProfileModal] = useState({
    open: false,
    candidateId: null,
    index: 0,
  });

  const user = getUserInfo();
  const isCse = user?.roles?.includes('ahedge_cse');

  const {
    data: requirementLocationsResponse,
    isLoading: isRequirementLocationsLoading,
    error: requirementLocationsError,
    refetch: refetchRequirementLocations,
  } = useRequirementLocations(opportunityId, {
    enabled: !!opportunityId,
    assignmentId,
  });

  let requirementLocations = [];
  let opportunityData = {};
  if (isCse) {
    requirementLocations =
      requirementLocationsResponse?.data?.requirement_locations || [];
    opportunityData =
      requirementLocationsResponse?.data?.requirement_info || {};
  } else {
    requirementLocations =
      requirementLocationsResponse?.data?.assignment_locations || [];
    opportunityData = requirementLocationsResponse?.data?.assignment_info || {};
  }

  useEffect(() => {
    if (!opportunityId) navigate('/opportunities');
  }, [opportunityId, navigate]);

  useEffect(() => {
    if (requirementLocationsError) {
      toast({
        title: 'Error',
        description:
          requirementLocationsError.message ||
          'Failed to fetch requirement locations',
        variant: 'destructive',
      });
    }
  }, [requirementLocationsError, toast]);

  useEffect(() => {
    if (!requirementLocations.length) return;
    setSelectedRequirementLocation((prev) => {
      // Re-sync to the fresh object for the same id rather than keeping
      // `prev` — a refetch (e.g. after a submit) returns updated
      // requested/submitted counts, and holding onto the stale object here
      // is why maxSelectable/the floating bar's "X of Y left" wouldn't
      // update even though the underlying data had.
      const stillPresent =
        prev &&
        requirementLocations.find(
          (loc) => loc.requirement_location_id === prev.requirement_location_id
        );
      if (stillPresent) return stillPresent;
      const fromParam = requirementLocations.find(
        (loc) =>
          String(loc.requirement_location_id) ===
          String(requirementLocationIdFromParams || '')
      );
      return fromParam || requirementLocations[0];
    });
  }, [requirementLocations, requirementLocationIdFromParams]);

  // Ranked match list — page / search / tier are in the queryKey, so switching
  // tab, searching, or paging refetches automatically (and shows in devtools).
  const {
    data: matchesResponse,
    isFetching: matchesFetching,
    refetch: refetchMatches,
  } = useRequirementMatches({
    opportunityId,
    page: currentPage,
    search: debouncedSearch,
    tier: activeTab,
    enabled: !!selectedRequirementLocation,
  });

  const matchesData = matchesResponse?.success ? matchesResponse.data : null;
  const candidates = matchesData?.candidates || [];
  const requirementData = matchesData?.requirement || null;
  const stats = matchesData?.stats || { all: 0, best: 0, near: 0, explore: 0 };
  const totalPages = matchesData?.pagination?.total_pages || 1;
  const loading = matchesFetching || isSubmitting;

  // Per-field enforcement modes (mode + allowed_modes) for this requirement's
  // constraints — drives the initial mode and which segments are selectable.
  const { data: constraintModesResponse } = useRequirementConstraintModes(
    opportunityId,
    { enabled: !!selectedRequirementLocation }
  );
  const constraintModesByField = useMemo(() => {
    const list = constraintModesResponse?.data?.constraints || [];
    return list.reduce((acc, c) => ({ ...acc, [c.field]: c }), {});
  }, [constraintModesResponse]);
  // "off" (remove the rule) is always offered, on top of whatever the API
  // lists in allowed_modes for that field.
  const allowedModesFor = (field) => {
    const entry = constraintModesByField[field];
    if (!entry) return undefined;
    return ['off', ...(entry.allowed_modes || [])];
  };
  // A constraint chip is shown only when the API marks it `applicable: true`
  // for this requirement — the source of truth for whether this field is in
  // play here at all, regardless of allowed_modes or the JD's own values.
  const isApplicable = (field) =>
    constraintModesByField[field]?.applicable === true;
  // The API always reports a `mode` (usually "flag"), even when no one has
  // configured this field — `mode_source: "default"` means that value is
  // just the backend's placeholder, not an active rule. Only take the literal
  // mode when a recruiter has actually set it (mode_source: "recruiter");
  // otherwise treat the field as Off.
  const effectiveModeFor = (field) => {
    const entry = constraintModesByField[field];
    if (!entry) return 'off';
    return entry.mode_source === 'recruiter' ? entry.mode : 'off';
  };

  // Debounce search input — reset to page 1 on new query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setCurrentPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Initialise rule modes when requirement / constraint-mode data loads.
  // Skills default locally (must-have → flag, nice-to-have → boost); the
  // three constraints the API supports (experience, ctc, notice) seed from
  // the constraint-modes response.
  useEffect(() => {
    if (!requirementData) return;
    const initial = {};
    (requirementData.must_have_skills || []).forEach((s) => {
      const label = typeof s === 'string' ? s : s?.name || '';
      if (label) initial[`req__${label}`] = 'flag';
    });
    (requirementData.nice_to_have_skills || []).forEach((s) => {
      const label = typeof s === 'string' ? s : s?.name || '';
      if (label) initial[`opt__${label}`] = 'boost';
    });
    // Seed exactly the fields the Constraints row will render — i.e. those
    // the API marks applicable:true for this requirement. The starting mode
    // is only the API's literal `mode` when a recruiter actually set it;
    // otherwise (mode_source: "default") it starts as Off.
    if (constraintModesByField.experience?.applicable) {
      initial.constraint__exp = effectiveModeFor('experience');
    }
    if (constraintModesByField.ctc?.applicable) {
      initial.constraint__ctc = effectiveModeFor('ctc');
    }
    if (constraintModesByField.notice?.applicable) {
      initial.constraint__notice = effectiveModeFor('notice');
    }
    setRules(initial);
  }, [requirementData, constraintModesByField]);

  const maxSelectable = useMemo(() => {
    if (!selectedRequirementLocation) return 0;
    const requested = Number(
      isCse
        ? (selectedRequirementLocation.calculated_headcount ?? 0)
        : (selectedRequirementLocation.candidates_requested ?? 0)
    );
    const submitted = Number(
      isCse
        ? (selectedRequirementLocation.profiles_submitted ?? 0)
        : (selectedRequirementLocation.candidates_submitted ?? 0)
    );
    return Math.max(0, requested - submitted);
  }, [selectedRequirementLocation, isCse]);

  const visibleVms = candidates;

  const tabCount = (key) => stats?.[key] ?? 0;

  const handleSelectCandidate = (id) => {
    setSelectedCandidates((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      // `maxSelectable &&` used to treat 0 as falsy and skip this guard
      // entirely, so a fully-filled location (0 slots left) let selection
      // through unbounded instead of blocking it — comparing the count
      // directly closes that gap.
      if (prev.length >= maxSelectable) {
        toast({
          title: 'Warning',
          description:
            maxSelectable <= 0
              ? 'All requested slots for this location have already been filled'
              : `You can select up to ${maxSelectable} candidate${maxSelectable > 1 ? 's' : ''} for this location`,
          variant: 'warning',
        });
        return prev;
      }
      return [...prev, id];
    });
  };

  const handleRequirementLocationChange = (value) => {
    const selected = requirementLocations.find(
      (loc) => String(loc.requirement_location_id) === value
    );
    setSelectedRequirementLocation(selected || null);
    setCurrentPage(1);
    setSelectedCandidates([]);
    const p = new URLSearchParams(searchParams);
    p.set('page', '1');
    setSearchParams(p);
  };

  // Only for the confirm-submit dialog — lets a TA double-check (or correct)
  // which location the selection is about to be submitted against, without
  // the header dropdown's side effects (that one resets the candidate list
  // and clears the selection, which would undo the very selection this
  // dialog is about to submit).
  const handleConfirmLocationChange = (value) => {
    const selected = requirementLocations.find(
      (loc) => String(loc.requirement_location_id) === value
    );
    setSelectedRequirementLocation(selected || null);
  };

  const submitCandidatesMutation = useSubmitCandidates();
  const submitSecondaryMutation = useSubmitSecondaryCandidates();

  const initiateSubmit = () => {
    if (selectedCandidates.length === 0) {
      toast({
        title: 'Warning',
        description: 'Please select at least one candidate',
        variant: 'warning',
      });
      return;
    }
    setShowConfirmSubmit(true);
  };

  const handleSubmitConfirmed = async () => {
    const rlId = selectedRequirementLocation?.requirement_location_id;
    if (isCse && !rlId) {
      toast({
        title: 'Warning',
        description: 'Please select a requirement location',
        variant: 'warning',
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const response = isCse
        ? await submitCandidatesMutation.mutateAsync({
            id: opportunityId,
            requirementLocationId: rlId,
            candidates: selectedCandidates,
          })
        : await submitSecondaryMutation.mutateAsync({
            assignmentId,
            requirementLocationId: rlId,
            candidates: selectedCandidates,
          });
      if (response.success) {
        toast({
          title: 'Success',
          description: 'Candidates submitted successfully',
        });
        setSelectedCandidates([]);
        setShowConfirmSubmit(false);
        // Only these two actually change as a result of a submit: the
        // location's requested/submitted counts (maxSelectable + the header
        // dropdown labels) and the ranked match list itself (submitted
        // candidates can shift tier/status). Nothing else on this page does.
        await Promise.all([refetchRequirementLocations(), refetchMatches()]);
      } else {
        throw new Error(response.message || 'Failed to submit candidates');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
      setShowConfirmSubmit(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Deliberately not `maxSelectable > 0 && ...` — that treated 0 remaining
  // slots as "no limit" (0 is falsy) and left every checkbox enabled on a
  // fully-filled location. Comparing the count directly disables them
  // correctly whether maxSelectable is 0 or has just been reached.
  const selectionFull = selectedCandidates.length >= maxSelectable;

  const renderRows = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-20 text-muted-foreground gap-2">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading candidates…
        </div>
      );
    }
    if (!selectedRequirementLocation) {
      return (
        <div className="py-16 text-center text-[13px] text-muted-foreground">
          Select a requirement location to view matches.
        </div>
      );
    }
    if (visibleVms.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 px-5 text-center">
          <div
            className="w-[72px] h-[72px] rounded-full flex items-center justify-center mb-5"
            style={{ background: 'var(--border)' }}
          >
            <AlertTriangle
              className="w-8 h-8 text-muted-foreground"
              strokeWidth={1.5}
            />
          </div>
          <p className="text-[15px] font-semibold text-foreground-strong mb-1.5">
            No candidates found
          </p>
          <p className="text-[13px] text-muted-foreground max-w-xs">
            No candidates match the current tab or search for this location.
          </p>
        </div>
      );
    }
    return visibleVms.map((c) => (
      <CandidateMatchRow
        key={c.candidate_id}
        vm={c}
        expanded={expandedId === c.candidate_id}
        onToggle={(id) => setExpandedId((cur) => (cur === id ? null : id))}
        selected={selectedCandidates.includes(c.candidate_id)}
        onToggleSelect={handleSelectCandidate}
        selectDisabled={selectionFull}
        showSelection={!isCse}
        onProfile={(id) => {
          const idx = visibleVms.findIndex((vm) => vm.candidate_id === id);
          setProfileModal({
            open: true,
            candidateId: id,
            index: idx < 0 ? 0 : idx,
          });
        }}
      />
    ));
  };

  return (
    <div>
      <PageHeader
        title="Candidate Matches"
        actions={
          <div className="flex items-center gap-2">
            <AppSelect
              options={requirementLocations.map((loc) => ({
                value: String(loc.requirement_location_id),
                label: `${loc.location_name} - ${loc.employment_type}`,
              }))}
              value={
                selectedRequirementLocation
                  ? String(selectedRequirementLocation.requirement_location_id)
                  : ''
              }
              onValueChange={handleRequirementLocationChange}
              disabled={
                isRequirementLocationsLoading ||
                requirementLocations.length === 0
              }
              placeholder={
                isRequirementLocationsLoading ? 'Loading…' : 'Select location'
              }
              className="w-[220px]"
            />
            <Button
              variant="outline"
              onClick={() => {
                const rlId =
                  selectedRequirementLocation?.requirement_location_id ??
                  requirementLocationIdFromParams;
                // CSE's own "submitted candidates" view is RequirementPipeline.jsx
                // (/candidates/list) — /assignment-pipeline is TA's pipeline
                // view and isn't meaningful for a CSE user.
                if (isCse) {
                  const p = new URLSearchParams({ status: 'submitted' });
                  if (opportunityId) p.set('opportunityId', opportunityId);
                  if (rlId) p.set('requirementLocationId', rlId);
                  navigate(`/candidates/list?${p.toString()}`);
                  return;
                }
                const p = new URLSearchParams({ status: 'new', opportunityId });
                if (assignmentId) p.set('assignmentId', assignmentId);
                if (rlId) p.set('requirementLocationId', rlId);
                navigate(`/assignment-pipeline?${p.toString()}`);
              }}
              className="h-9 rounded-lg px-3 text-[13px] gap-1.5"
            >
              <ListChecks className="h-4 w-4" />
              View Submitted
            </Button>
          </div>
        }
      />

      <PageContainer className="text-left">
        {/* Page heading */}
        <div className="mb-6">
          <h1 className="text-[22px] font-bold tracking-[-0.04em] text-foreground-strong m-0 mb-1">
            {opportunityData?.title ||
              requirementData?.title ||
              'Candidate Matches'}
          </h1>
          <p className="text-[13.5px] text-muted-foreground m-0 leading-[1.55]">
            Candidates ranked by fit against role requirements, skills, and
            availability signals.
          </p>
        </div>

        {/* Match summary */}
        <div className="mb-6">
          <button
            type="button"
            onClick={() => setSummaryOpen((v) => !v)}
            className="flex items-center justify-between w-full mb-3 cursor-pointer bg-transparent border-0 p-0"
          >
            <span className="text-[12px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              Match Summary
            </span>
            <span
              className="w-6 h-6 rounded-md flex items-center justify-center text-muted-foreground hover:bg-border transition-transform"
              style={{ transform: summaryOpen ? 'none' : 'rotate(-90deg)' }}
            >
              <ChevronDown style={{ width: 16, height: 16, strokeWidth: 2 }} />
            </span>
          </button>
          <div
            className={`grid transition-all duration-300 ease-in-out ${
              summaryOpen
                ? 'grid-rows-[1fr] opacity-100'
                : 'grid-rows-[0fr] opacity-0'
            }`}
          >
            <div className="overflow-hidden">
              <div className="grid grid-cols-4 gap-[14px] max-[900px]:grid-cols-2">
                <SummaryCard
                  icon={
                    <Users
                      style={{ width: 20, height: 20, strokeWidth: 1.8 }}
                    />
                  }
                  iconColor="var(--primary)"
                  label="Total Evaluated"
                  value={stats.all}
                  sub="from candidate pool"
                />
                <SummaryCard
                  icon={
                    <Star style={{ width: 20, height: 20, strokeWidth: 1.8 }} />
                  }
                  iconColor="var(--ds-success)"
                  label="Best Match"
                  value={stats.best}
                  sub={`score ≥ ${TIERS.best.min}`}
                />
                <SummaryCard
                  icon={
                    <CircleDashed
                      style={{ width: 20, height: 20, strokeWidth: 1.8 }}
                    />
                  }
                  iconColor="var(--ds-warning)"
                  label="Near Match"
                  value={stats.near}
                  sub={`score ${TIERS.near.min} – ${TIERS.best.min - 1}`}
                />
                <SummaryCard
                  icon={
                    <Compass
                      style={{ width: 20, height: 20, strokeWidth: 1.8 }}
                    />
                  }
                  iconColor="var(--muted-foreground)"
                  label="Explore"
                  value={stats.explore}
                  sub={`score < ${TIERS.near.min}`}
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── Match Rules (collapsible) ── */}
        {requirementData && (
          <div className="mb-5">
            <button
              type="button"
              onClick={() => setRulesOpen((v) => !v)}
              className="flex items-center gap-3 w-full px-2 py-[7px] -mx-2 rounded-lg hover:bg-black/[0.025] dark:hover:bg-white/[0.03] transition-colors"
            >
              <span className="text-[11px] font-[650] uppercase tracking-[0.07em] text-muted-foreground whitespace-nowrap">
                Match Rules
              </span>
              {!rulesOpen && requirementData.must_have_skills?.length > 0 && (
                <span className="text-[12px] text-muted-foreground">
                  <span className="font-[600] text-foreground">
                    {requirementData.must_have_skills.length}
                  </span>{' '}
                  must-have
                </span>
              )}
              {!rulesOpen &&
                requirementData.must_have_skills?.length > 0 &&
                requirementData.nice_to_have_skills?.length > 0 && (
                  <span className="text-muted-foreground/40 text-[12px]">
                    ·
                  </span>
                )}
              {!rulesOpen &&
                requirementData.nice_to_have_skills?.length > 0 && (
                  <span className="text-[12px] text-muted-foreground">
                    <span className="font-[600] text-foreground">
                      {requirementData.nice_to_have_skills.length}
                    </span>{' '}
                    nice-to-have
                  </span>
                )}
              <ChevronDown
                className={cn(
                  'ml-auto w-[15px] h-[15px] text-muted-foreground transition-transform duration-200 flex-shrink-0',
                  !rulesOpen && '-rotate-90'
                )}
                strokeWidth={2}
              />
            </button>

            <div
              className={cn(
                'grid transition-all duration-300 ease-in-out',
                rulesOpen
                  ? 'grid-rows-[1fr] opacity-100 mt-1.5'
                  : 'grid-rows-[0fr] opacity-0 mt-0'
              )}
            >
              <div className="overflow-hidden">
                <div className="bg-card rounded-xl border border-border shadow-[0_1px_3px_rgba(0,0,0,0.04)] divide-y divide-border">
                  {/* Domain row */}
                  {requirementData.domains?.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap px-5 py-3 !pb-0">
                      <span className="text-[10.5px] font-[600] uppercase tracking-[0.06em] text-muted-foreground whitespace-nowrap">
                        Domain
                      </span>
                      {requirementData.domains.map((d) => {
                        const isPrimary = d === requirementData.primary_domain;
                        return (
                          <span
                            key={d}
                            className={cn(
                              'inline-flex items-center gap-[5px] px-[10px] py-[5px] rounded-[7px] text-[12px] border',
                              isPrimary
                                ? 'bg-brand-bg-subtle text-primary border-primary/20 font-[600]'
                                : 'bg-muted text-muted-foreground border-border font-[500]'
                            )}
                          >
                            {isPrimary && (
                              <Star
                                className="w-[11px] h-[11px]"
                                strokeWidth={2}
                              />
                            )}
                            {d}
                            {isPrimary && (
                              <span className="text-[9px] font-[700] tracking-[0.05em] px-[5px] py-px rounded-[3px] bg-primary/10 text-primary">
                                PRIMARY
                              </span>
                            )}
                          </span>
                        );
                      })}
                    </div>
                  )}

                  {/* Must-have + Nice-to-have rows */}
                  {(requirementData.must_have_skills?.length > 0 ||
                    requirementData.nice_to_have_skills?.length > 0) && (
                    <div className="flex flex-col gap-2.5 px-5 py-3 !border-t-0">
                      {requirementData.must_have_skills?.length > 0 && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10.5px] font-[600] uppercase tracking-[0.06em] text-muted-foreground whitespace-nowrap">
                            Must-Have
                          </span>
                          {requirementData.must_have_skills.map((s, i) => {
                            const skillLabel =
                              typeof s === 'string' ? s : s?.name || '';
                            const isUnresolved =
                              requirementData.unresolved_skills?.includes(
                                skillLabel
                              );
                            return (
                              <RuleChip
                                key={skillLabel || i}
                                label={skillLabel}
                                isUnresolved={isUnresolved}
                                mode={rules[`req__${skillLabel}`] || 'flag'}
                                onModeChange={(m) =>
                                  setRuleMode(`req__${skillLabel}`, m)
                                }
                                interactive={false}
                                className="px-[10px] py-[5px] rounded-[7px] text-[12px] font-[500] bg-brand-bg-subtle text-primary border border-primary/15"
                              >
                                {skillLabel}
                                {isUnresolved && (
                                  <Info
                                    className="w-[11px] h-[11px] text-primary/50 flex-shrink-0"
                                    strokeWidth={2}
                                  />
                                )}
                                <span className="text-[9px] font-[700] tracking-[0.04em] px-[5px] py-px rounded-[3px] bg-primary/10 text-primary">
                                  REQ
                                </span>
                              </RuleChip>
                            );
                          })}
                        </div>
                      )}

                      {requirementData.nice_to_have_skills?.length > 0 && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10.5px] font-[600] uppercase tracking-[0.06em] text-muted-foreground whitespace-nowrap">
                            Nice-to-Have
                          </span>
                          {requirementData.nice_to_have_skills.map((s, i) => {
                            const skillLabel =
                              typeof s === 'string' ? s : s?.name || '';
                            const isUnresolved =
                              requirementData.unresolved_skills?.includes(
                                skillLabel
                              );
                            return (
                              <RuleChip
                                key={skillLabel || i}
                                label={skillLabel}
                                isUnresolved={isUnresolved}
                                mode={rules[`opt__${skillLabel}`] || 'boost'}
                                onModeChange={(m) =>
                                  setRuleMode(`opt__${skillLabel}`, m)
                                }
                                interactive={false}
                                className="px-[10px] py-[5px] rounded-[7px] text-[12px] font-[500] bg-muted text-muted-foreground border border-border"
                              >
                                {skillLabel}
                                {isUnresolved && (
                                  <Info
                                    className="w-[11px] h-[11px] opacity-40 flex-shrink-0"
                                    strokeWidth={2}
                                  />
                                )}
                                <span className="text-[9px] font-[700] tracking-[0.04em] px-[5px] py-px rounded-[3px] bg-border text-muted-foreground">
                                  OPT
                                </span>
                              </RuleChip>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Constraints row — a chip shows only when the API marks
                      that field applicable:true for this requirement.
                      Location has no entry in constraint-modes at all, so it
                      never shows. */}
                  {(isApplicable('experience') ||
                    isApplicable('ctc') ||
                    isApplicable('notice')) && (
                    <div className="flex items-center gap-2 flex-wrap px-5 py-3 !border-t-0 !pt-0">
                      <span className="text-[10.5px] font-[600] uppercase tracking-[0.06em] text-muted-foreground whitespace-nowrap flex items-center gap-1.5">
                        Constraints
                        <HowModesWork />
                      </span>
                      {isApplicable('experience') && (
                        <RuleChip
                          label="Experience"
                          field="experience"
                          requirementId={opportunityId}
                          mode={rules.constraint__exp || 'flag'}
                          onModeChange={(m) =>
                            setRuleMode('constraint__exp', m)
                          }
                          allowedModes={allowedModesFor('experience')}
                          className="gap-[6px] px-[9px] py-[4px] rounded-[6px] text-[12px] font-[500] text-muted-foreground"
                        >
                          <Briefcase
                            className="w-[12px] h-[12px]"
                            strokeWidth={1.8}
                          />
                          <span className="font-[600] text-foreground">
                            {requirementData.experience_range
                              ? formatExpRange(requirementData.experience_range)
                              : 'Not specified'}
                          </span>
                        </RuleChip>
                      )}
                      {isApplicable('ctc') && (
                        <RuleChip
                          label="Compensation"
                          field="ctc"
                          requirementId={opportunityId}
                          mode={rules.constraint__ctc || 'flag'}
                          onModeChange={(m) =>
                            setRuleMode('constraint__ctc', m)
                          }
                          allowedModes={allowedModesFor('ctc')}
                          className="gap-[6px] px-[9px] py-[4px] rounded-[6px] text-[12px] font-[500] text-muted-foreground"
                        >
                          <span className="font-[600] text-foreground">
                            {requirementData.salary_range || 'Not specified'}
                          </span>
                        </RuleChip>
                      )}
                      {isApplicable('notice') && (
                        <RuleChip
                          label="Notice period"
                          field="notice"
                          requirementId={opportunityId}
                          mode={rules.constraint__notice || 'flag'}
                          onModeChange={(m) =>
                            setRuleMode('constraint__notice', m)
                          }
                          allowedModes={allowedModesFor('notice')}
                          className="gap-[6px] px-[9px] py-[4px] rounded-[6px] text-[12px] font-[500] text-muted-foreground"
                        >
                          <Clock
                            className="w-[12px] h-[12px]"
                            strokeWidth={1.8}
                          />
                          {requirementData.max_notice_days ? (
                            <>
                              {`≤ `}
                              <span className="font-[600] text-foreground">
                                {requirementData.max_notice_days}d
                              </span>
                              {` notice`}
                            </>
                          ) : (
                            <span className="text-foreground">
                              Not specified
                            </span>
                          )}
                        </RuleChip>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center border-b border-border mb-0">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => {
                setActiveTab(t.key);
                setCurrentPage(1);
              }}
              className={cn(
                'inline-flex items-center gap-[7px] px-5 py-[10px] text-[13px] font-medium border-b-2 -mb-px transition-colors',
                activeTab === t.key
                  ? 'text-primary font-semibold border-primary'
                  : 'text-muted-foreground border-transparent hover:text-foreground'
              )}
            >
              {t.label}
              <span
                className={cn(
                  'text-[11px] font-semibold px-[7px] py-px rounded-[10px] tabular-nums leading-[1.5]',
                  activeTab === t.key
                    ? 'bg-brand-bg-subtle text-primary'
                    : 'bg-border text-muted-foreground'
                )}
              >
                {tabCount(t.key)}
              </span>
            </button>
          ))}
        </div>

        {/* Filter bar */}
        <div className="flex items-center mt-4 mb-4">
          <SearchInput
            value={searchQuery}
            onValueChange={setSearchQuery}
            placeholder="Search candidates…"
            className="ml-auto w-[240px]"
          />
        </div>

        {/* List */}
        <div className="bg-card rounded-xl border border-border shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden">
          {renderRows()}

          {!loading && tabCount(activeTab) > 0 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-border">
              <span className="text-[12px] text-muted-foreground">
                Showing {visibleVms.length} of {tabCount(activeTab)} candidates
              </span>
              {totalPages > 1 && (
                <div className="flex gap-1">
                  <button
                    type="button"
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                    className="w-[30px] h-[30px] rounded-md border border-input bg-card text-[12px] text-muted-foreground disabled:opacity-40 hover:enabled:border-primary hover:enabled:text-primary transition-colors"
                  >
                    ‹
                  </button>
                  <span className="w-[30px] h-[30px] rounded-md bg-primary text-white text-[12px] font-[550] flex items-center justify-center">
                    {currentPage}
                  </span>
                  <button
                    type="button"
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage((p) => p + 1)}
                    className="w-[30px] h-[30px] rounded-md border border-input bg-card text-[12px] text-muted-foreground disabled:opacity-40 hover:enabled:border-primary hover:enabled:text-primary transition-colors"
                  >
                    ›
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </PageContainer>

      {/* Floating bulk-selection bar — CSE browses matches read-only from
          this page (no select/submit action of their own here), so this
          only ever mounts for TA. Stays mounted always and slides via
          translate-y based on selection count, same approach as the
          Assignment Pipeline's own ManualActionsBar, so the transition
          actually animates instead of the bar just appearing/disappearing. */}
      {!isCse && (
        <div
          className={cn(
            'fixed left-1/2 bottom-[22px] z-40 flex items-center gap-2.5 rounded-xl border border-border bg-card px-4 py-2.5 shadow-[0_10px_30px_rgba(0,0,0,0.18),0_1px_4px_rgba(0,0,0,0.08)] transition-transform duration-200 ease-out',
            selectedCandidates.length > 0
              ? '-translate-x-1/2 translate-y-0'
              : '-translate-x-1/2 translate-y-[130%]'
          )}
        >
          <span className="text-[12.5px] font-semibold text-muted-foreground whitespace-nowrap">
            <b className="font-mono text-primary">
              {selectedCandidates.length}
            </b>{' '}
            selected
            {maxSelectable
              ? ` · ${maxSelectable - selectedCandidates.length} of ${maxSelectable} left`
              : ''}
          </span>
          <span className="w-px h-5 bg-border shrink-0" />
          <Button
            size="sm"
            onClick={initiateSubmit}
            className="h-8 gap-1.5 px-3 text-[12.5px]"
          >
            <Users className="h-3.5 w-3.5" />
            Submit Selected
          </Button>
          <button
            type="button"
            onClick={() => setSelectedCandidates([])}
            aria-label="Clear selection"
            className="w-8 h-8 rounded-lg border border-border text-muted-foreground hover:text-primary hover:border-primary transition-colors inline-flex items-center justify-center shrink-0"
          >
            <X className="h-[15px] w-[15px]" strokeWidth={1.9} />
          </button>
        </div>
      )}

      <Alert
        isOpen={showConfirmSubmit}
        onClose={() => setShowConfirmSubmit(false)}
        title="Confirm Submit"
        description={`Are you sure you want to submit ${selectedCandidates.length} selected candidate${selectedCandidates.length > 1 ? 's' : ''}?`}
        cancelText="Cancel"
        continueText="Submit"
        onCancel={() => setShowConfirmSubmit(false)}
        onContinue={handleSubmitConfirmed}
        // TA-only: a last chance to confirm (or correct) which requirement
        // location this selection submits against, right before it happens.
        customContent={
          !isCse &&
          requirementLocations.length > 0 && (
            <div className="space-y-1.5">
              <span className="block text-[12.5px] font-semibold text-foreground-strong">
                Requirement location
              </span>
              <AppSelect
                options={requirementLocations.map((loc) => ({
                  value: String(loc.requirement_location_id),
                  label: `${loc.location_name} - ${loc.employment_type}`,
                }))}
                value={
                  selectedRequirementLocation
                    ? String(
                        selectedRequirementLocation.requirement_location_id
                      )
                    : ''
                }
                onValueChange={handleConfirmLocationChange}
                placeholder="Select location"
                className="w-full"
              />
            </div>
          )
        }
      />

      <CandidateProfileModal
        isOpen={profileModal.open}
        onClose={() => setProfileModal((p) => ({ ...p, open: false }))}
        candidateId={profileModal.candidateId}
        opportunityId={opportunityId}
        candidates={visibleVms}
        currentIndex={profileModal.index}
        // Same convention as AssignmentPipeline.jsx — carries this page's own
        // assignmentId/requirementLocationId onto the "Full page" link's URL
        // so Preview.jsx's breadcrumb can route a TA back to their own
        // assignment pipeline instead of CSE's /candidates/list.
        fullPageQuery={{
          ...(assignmentId ? { assignmentId } : {}),
          ...(selectedRequirementLocation?.requirement_location_id
            ? {
                requirementLocationId:
                  selectedRequirementLocation.requirement_location_id,
              }
            : {}),
        }}
        onNavigate={(newIndex) =>
          setProfileModal({
            open: true,
            candidateId: visibleVms[newIndex]?.candidate_id ?? null,
            index: newIndex,
          })
        }
        candidateScore={visibleVms[profileModal.index]?.score}
        candidateTier={visibleVms[profileModal.index]?.tier}
        // CandidateProfileModal only renders its "Select to shortlist"
        // footer button when onToggleSelect is actually passed — omitting
        // all three for CSE hides it the same way the row/expanded-panel
        // selection controls are hidden above.
        isSelected={
          isCse
            ? undefined
            : selectedCandidates.includes(profileModal.candidateId)
        }
        onToggleSelect={isCse ? undefined : handleSelectCandidate}
        selectDisabled={isCse ? undefined : selectionFull}
      />
    </div>
  );
};

export default SubmitCandidate;
